import { useState } from 'react';
import { Check, Sparkles, Zap, Infinity as InfinityIcon, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface PricingModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const PLANS = [
    {
        id: 'free',
        name: 'Free',
        price: '$0',
        period: '/month',
        credits: '5 credits',
        description: 'Great for trying out DaddyCad',
        features: [
            '5 credits per month',
            'Basic model generation',
            'STL export',
            'Community support',
        ],
        icon: Sparkles,
        color: 'text-adam-neutral-300',
        bgColor: 'bg-adam-neutral-800',
        borderColor: 'border-adam-neutral-600',
    },
    {
        id: 'pro',
        name: 'Pro',
        price: '$10',
        period: '/month',
        credits: '100 credits',
        description: 'For regular creators',
        features: [
            '100 credits per month',
            'Priority generation',
            'All export formats',
            'Email support',
            'Credit rollover (up to 50)',
        ],
        icon: Zap,
        color: 'text-adam-blue',
        bgColor: 'bg-adam-blue/10',
        borderColor: 'border-adam-blue/50',
        popular: true,
    },
    {
        id: 'unlimited',
        name: 'Unlimited',
        price: '$30',
        period: '/month',
        credits: 'Unlimited',
        description: 'For power users',
        features: [
            'Unlimited generations',
            'Fastest processing',
            'All export formats',
            'Priority support',
            'Early access to features',
        ],
        icon: InfinityIcon,
        color: 'text-purple-400',
        bgColor: 'bg-purple-500/10',
        borderColor: 'border-purple-500/50',
    },
];

export function PricingModal({ open, onOpenChange }: PricingModalProps) {
    const { user } = useAuth();
    const { subscriptionTier } = useCredits();
    const { toast } = useToast();
    const [loadingTier, setLoadingTier] = useState<string | null>(null);

    const handleSubscribe = async (tier: string) => {
        if (tier === 'free') return;
        if (!user || user.is_anonymous) {
            // Show auth modal instead
            return;
        }

        setLoadingTier(tier);
        try {
            const { data, error } = await supabase.functions.invoke('create-checkout', {
                body: {
                    tier,
                    successUrl: `${window.location.origin}/daddycad?checkout=success`,
                    cancelUrl: `${window.location.origin}/daddycad?checkout=canceled`,
                },
            });

            if (error) throw error;

            if (data?.url) {
                window.location.href = data.url;
            }
        } catch (error: any) {
            console.error('Error creating checkout session:', error);

            let errorMessage = error.message || 'Failed to start checkout';

            // Try to extract more details if available
            if (error.context && error.context.response) {
                try {
                    const errorBody = await error.context.response.json();
                    if (errorBody.error) {
                        errorMessage = `${errorMessage} (${errorBody.error})`;
                    }
                } catch (e) {
                    // response might not be json
                }
            }

            toast({
                title: 'Checkout Error',
                description: errorMessage,
                variant: 'destructive',
            });
        } finally {
            setLoadingTier(null);
        }
    };

    const handleManageSubscription = async () => {
        setLoadingTier('manage');
        try {
            const { data, error } = await supabase.functions.invoke('billing-portal', {
                body: {
                    returnUrl: window.location.href,
                },
            });

            if (error) throw error;

            if (data?.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error('Error opening billing portal:', error);
        } finally {
            setLoadingTier(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl border-adam-neutral-700 bg-adam-bg-dark">
                <DialogHeader>
                    <DialogTitle className="text-center text-2xl font-bold text-adam-text-primary">
                        Choose Your Plan
                    </DialogTitle>
                    <p className="text-center text-adam-text-secondary">
                        Unlock more generations and features with a subscription
                    </p>
                </DialogHeader>

                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                    {PLANS.map((plan) => {
                        const Icon = plan.icon;
                        const isCurrent = subscriptionTier === plan.id;
                        const isLoading = loadingTier === plan.id;

                        return (
                            <div
                                key={plan.id}
                                className={cn(
                                    'relative flex flex-col rounded-xl border p-6 transition-all',
                                    plan.bgColor,
                                    plan.borderColor,
                                    isCurrent && 'ring-2 ring-adam-blue'
                                )}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <span className="rounded-full bg-adam-blue px-3 py-1 text-xs font-medium text-white">
                                            Most Popular
                                        </span>
                                    </div>
                                )}

                                <div className="mb-4 flex items-center gap-2">
                                    <Icon className={cn('h-5 w-5', plan.color)} />
                                    <h3 className={cn('text-lg font-semibold', plan.color)}>
                                        {plan.name}
                                    </h3>
                                </div>

                                <div className="mb-2">
                                    <span className="text-3xl font-bold text-adam-text-primary">
                                        {plan.price}
                                    </span>
                                    <span className="text-adam-text-secondary">{plan.period}</span>
                                </div>

                                <p className="mb-4 text-sm text-adam-text-secondary">
                                    {plan.description}
                                </p>

                                <div className="mb-4 rounded-lg bg-adam-neutral-800/50 px-3 py-2">
                                    <span className={cn('text-sm font-medium', plan.color)}>
                                        {plan.credits}
                                    </span>
                                </div>

                                <ul className="mb-6 flex-1 space-y-2">
                                    {plan.features.map((feature, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-2 text-sm text-adam-text-secondary"
                                        >
                                            <Check className={cn('mt-0.5 h-4 w-4 shrink-0', plan.color)} />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                {isCurrent ? (
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={plan.id !== 'free' ? handleManageSubscription : undefined}
                                        disabled={plan.id === 'free' || loadingTier === 'manage'}
                                    >
                                        {loadingTier === 'manage' ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : plan.id === 'free' ? (
                                            'Current Plan'
                                        ) : (
                                            'Manage Subscription'
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        className={cn(
                                            'w-full',
                                            plan.id === 'pro' && 'bg-adam-blue hover:bg-adam-blue/90',
                                            plan.id === 'unlimited' && 'bg-purple-500 hover:bg-purple-600'
                                        )}
                                        onClick={() => handleSubscribe(plan.id)}
                                        disabled={isLoading || plan.id === 'free'}
                                    >
                                        {isLoading ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : plan.id === 'free' ? (
                                            'Free Forever'
                                        ) : (
                                            'Subscribe'
                                        )}
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                </div>

                <p className="mt-4 text-center text-xs text-adam-text-muted">
                    All plans include a 7-day money-back guarantee. Cancel anytime.
                </p>
            </DialogContent>
        </Dialog>
    );
}
